import { CopyButton } from "@/components/tool-kit/primitives";
import { t } from "@/lib/i18n/interpolate";
import {
  configuration,
  configurationText,
  expandedConfiguration,
  type PeriodicElement,
  type Subshell,
} from "@/lib/tool-kit/chem-reference/periodic";

export interface DetailStrings {
  atomicNumber: string;
  atomicWeight: string;
  weightUnit: string;
  massNumberNote: string;
  group: string;
  groupNone: string;
  period: string;
  block: string;
  blockNames: Record<string, string>;
  category: string;
  classLabels: Record<string, string>;
  electronegativity: string;
  electronegativityNone: string;
  configuration: string;
  expanded: string;
  predictedNote: string;
  discovered: string;
  ancient: string;
  copyDetails: string;
  copied: string;
  copyText: string;
}

/** A configuration with the subshell counts as superscripts: 3d⁶ 4s². */
export function ShellList({ shells }: { shells: readonly Subshell[] }) {
  return (
    <>
      {shells.map((shell, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: positional subshells of one configuration
        <span className="whitespace-nowrap" key={index}>
          {index > 0 ? " " : ""}
          {shell.n}
          {shell.l}
          <sup>{shell.count}</sup>
        </span>
      ))}
    </>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <li className="space-y-0.5 rounded-lg border border-border bg-card p-3">
      <span className="block text-muted-foreground text-xs">{label}</span>
      <span className="block break-words font-medium text-foreground text-sm">
        {children}
      </span>
    </li>
  );
}

export function PeriodicDetail({
  element,
  categoryId,
  strings,
  fmt,
}: {
  element: PeriodicElement;
  categoryId: string;
  strings: DetailStrings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}) {
  const config = configuration(element.z);
  const weight = fmt(element.weight, { maximumFractionDigits: 4 });
  const weightText = element.massNumber ? `[${weight}]` : weight;
  const pauling =
    element.electronegativity === null
      ? strings.electronegativityNone
      : fmt(element.electronegativity, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  const discovered =
    element.discovered === null ? strings.ancient : String(element.discovered);
  const summary = t(strings.copyText, {
    name: element.name,
    symbol: element.symbol,
    z: String(element.z),
    weight: weightText,
    config: configurationText(config),
    en: pauling,
  });
  return (
    <section aria-live="polite" className="space-y-4">
      <div className="flex items-center gap-4 rounded-lg border border-primary/30 bg-primary/5 p-4">
        <p className="font-bold text-4xl text-foreground">{element.symbol}</p>
        <div>
          <p className="font-semibold text-foreground text-lg">
            {element.name}
          </p>
          <p className="text-muted-foreground text-sm">
            {strings.classLabels[categoryId]}
          </p>
        </div>
      </div>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Row label={strings.atomicNumber}>{element.z}</Row>
        <Row label={`${strings.atomicWeight} (${strings.weightUnit})`}>
          {weightText}
        </Row>
        <Row label={strings.group}>{element.group ?? strings.groupNone}</Row>
        <Row label={strings.period}>{element.period}</Row>
        <Row label={strings.block}>{strings.blockNames[element.block]}</Row>
        <Row label={strings.electronegativity}>{pauling}</Row>
        <Row label={strings.discovered}>{discovered}</Row>
      </ul>
      <ul className="grid gap-3">
        <Row label={strings.configuration}>
          {config.core ? `[${config.core}] ` : ""}
          <ShellList shells={config.shells} />
        </Row>
        <Row label={strings.expanded}>
          <ShellList shells={expandedConfiguration(element.z)} />
        </Row>
      </ul>
      {element.massNumber ? (
        <p className="text-muted-foreground text-xs">
          {strings.massNumberNote}
        </p>
      ) : null}
      {config.predicted ? (
        <p className="text-muted-foreground text-xs">{strings.predictedNote}</p>
      ) : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyDetails}
        text={summary}
      />
    </section>
  );
}
